import {
  Code as CodeIcon,
  Favorite,
  FavoriteBorder,
  Label as LabelIcon,
  Launch as LaunchIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import {FC, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {styled} from 'styled-components';

import {ScraperItem} from '@shared/models/parser';

import {ImageCarousel} from '@website_frontend/components/image_carousel';
import {MapPopover} from '@website_frontend/components/map_popover';
import {TagManagementSheet} from '@website_frontend/components/tag_management_sheet';
import {useDescription} from '@website_frontend/lib/db_cache_descriptions';
import {isItemLiked, toggleLikedItem, useTagsData} from '@website_frontend/lib/user_data_storage';

interface HousePreviewCardViewProps {
  item: ScraperItem;
}

export const HousePreviewCardView: FC<HousePreviewCardViewProps> = props => {
  const {item} = props;
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [isLiked, setIsLiked] = useState(() => isItemLiked(item?.id));
  const [chipsPopoverOpen, setChipsPopoverOpen] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [tagManagementOpen, setTagManagementOpen] = useState(false);
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const description = useDescription(item.id);
  const tagsData = useTagsData();
  const itemTags = useMemo(() => tagsData[item?.id] ?? [], [tagsData, item?.id]);

  const images = useMemo(() => item?.images?.filter(Boolean) ?? [], [item?.images]);
  const priceLabel = useMemo(() => {
    if (item?.price == null) return 'Prix non disponible';
    const formatted = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0}).format(
      item?.price
    );
    return item?.currency ? `${formatted} ${item?.currency}` : formatted;
  }, [item?.price, item?.currency]);

  const metaChips = useMemo(() => {
    const chips: string[] = [];
    const isHouseLike = /maison|villa|mas/i.test(item?.typeLabel ?? '');
    if (item?.areaM2) chips.push(`${item?.areaM2} m²`);
    if (item?.nbBedrooms) chips.push(`${item?.nbBedrooms} ch`);
    if (item?.nbRooms) chips.push(`${item?.nbRooms} p`);
    if (!isHouseLike && item?.parking) chips.push('Parking');
    if (item?.landSurface) chips.push(`Terrain ${item?.landSurface} m²`);
    if (!isHouseLike && item?.floorNb != null) chips.push(`Étage ${item?.floorNb}`);
    if (item?.energyCertificate) chips.push(`DPE ${item?.energyCertificate}`);
    return chips;
  }, [
    item?.areaM2,
    item?.nbBedrooms,
    item?.nbRooms,
    item?.parking,
    item?.landSurface,
    item?.floorNb,
    item?.energyCertificate,
    item?.typeLabel,
  ]);

  const locationLabel =
    item?.locationLabel ??
    [item?.city, item?.estatePostalCode, item?.country].filter(Boolean).join(', ');

  const handleLikeToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newLikedState = toggleLikedItem(item?.id);
    setIsLiked(newLikedState);
  };

  // Check for overflow in chips container
  const checkOverflow = useMemo(
    () => () => {
      if (chipsContainerRef.current) {
        const {scrollWidth, clientWidth} = chipsContainerRef.current;
        setHasOverflow(scrollWidth > clientWidth);
      }
    },
    []
  );

  useLayoutEffect(() => {
    checkOverflow();
  });

  useEffect(() => {
    // Recheck on window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  const handleChipsClick = () => {
    if (hasOverflow) {
      setChipsPopoverOpen(true);
    }
  };

  return (
    <Wrapper>
      {/* <ImageWrapper> */}
      <ImageCarousel images={images} title={item?.title} />
      {/* </ImageWrapper> */}
      <Stack padding={2} gap={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {/* Price and type label */}
          <Stack direction="row" alignItems="center" flexGrow={1} width={0}>
            <Typography variant="h6" noWrap sx={{overflow: 'hidden', textOverflow: 'ellipsis'}}>
              {priceLabel}
            </Typography>
            {item?.typeLabel && (
              <Chip
                size="small"
                variant="outlined"
                label={item?.typeLabel}
                sx={{flexShrink: 0, ml: 1}}
              />
            )}
          </Stack>
          <LikeButtonWrapper>
            <LikeButton
              onClick={handleLikeToggle}
              size="large"
              sx={{
                backgroundColor: isLiked ? '#f44336' : '#ffffff4d',
                p: 2,
                '&:hover': {
                  backgroundColor: isLiked ? '#f44336' : '#ffffff80',
                },
              }}
              aria-label={isLiked ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              {isLiked ? (
                <Favorite sx={{color: 'white', fontSize: 20}} />
              ) : (
                <FavoriteBorder sx={{color: '#f4433680', fontSize: 20}} />
              )}
            </LikeButton>
          </LikeButtonWrapper>
        </Stack>

        <Stack gap={0.5}>
          {/* Title */}
          <Typography variant="subtitle1" lineHeight={1} noWrap>
            {item?.title}
          </Typography>

          {/* Description */}
          {description && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {description}
            </Typography>
          )}
        </Stack>

        {/* Meta chips */}
        {metaChips.length ? (
          <Stack
            ref={chipsContainerRef}
            direction="row"
            spacing={0.75}
            useFlexGap
            onClick={handleChipsClick}
            sx={{
              overflow: 'hidden',
              minHeight: '24px', // Fixed height for chips row (Chip height + spacing)
              cursor: hasOverflow ? 'pointer' : 'default',
              position: 'relative',
              '&::after': hasOverflow
                ? {
                    content: '""',
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '20px',
                    background: 'linear-gradient(to right, transparent, white)',
                    pointerEvents: 'none',
                  }
                : {},
            }}
          >
            {metaChips.map(text => (
              <Chip key={text} size="small" variant="outlined" label={text} sx={{flexShrink: 0}} />
            ))}
          </Stack>
        ) : (
          <Stack sx={{minHeight: '24px'}} />
        )}
      </Stack>

      <BottomStack direction="row" px={2} alignItems="center" justifyContent="space-between">
        {/* Location label */}
        <Typography
          variant="body2"
          color="text.secondary"
          lineHeight={1}
          noWrap
          sx={{flex: 1, minWidth: 0}}
        >
          {locationLabel ?? ''}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {/* Tags section */}
          {(itemTags.length > 0 || isLiked) && (
            <Stack direction="row" alignItems="center" gap={0.5} sx={{flexShrink: 0}}>
              {itemTags.length > 0 && (
                <Stack direction="row" gap={0.25} sx={{maxWidth: '150px', overflow: 'hidden'}}>
                  {itemTags.slice(0, 2).map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                      sx={{height: '20px', fontSize: '0.7rem', flexShrink: 0}}
                    />
                  ))}
                  {itemTags.length > 2 && (
                    <Chip
                      label={`+${itemTags.length - 2}`}
                      size="small"
                      variant="outlined"
                      sx={{height: '20px', fontSize: '0.7rem', flexShrink: 0}}
                    />
                  )}
                </Stack>
              )}
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  setTagManagementOpen(true);
                }}
                size="large"
                sx={{mr: -2}}
                title="Gérer les tags"
              >
                <LabelIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
          {/* Action icons */}
          <Stack direction="row" alignItems="center" gap={0.5}>
            <IconButton onClick={() => setMapOpen(true)} size="large" sx={{mr: item?.url ? -2 : 0}}>
              <MapIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setDebugModalOpen(true)}
              size="large"
              sx={{mr: item?.url ? -2 : 0}}
            >
              <CodeIcon fontSize="small" />
            </IconButton>
            {item?.url && (
              <IconButton
                component="a"
                href={item?.url}
                target="_blank"
                rel="noopener noreferrer"
                size="large"
                sx={{mr: -2}}
              >
                <LaunchIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </BottomStack>

      {item && <MapPopover item={item} open={mapOpen} onClose={() => setMapOpen(false)} />}
      {item && (
        <TagManagementSheet
          open={tagManagementOpen}
          onClose={() => setTagManagementOpen(false)}
          itemId={item.id}
        />
      )}
      {metaChips.length > 0 && (
        <Popover
          open={chipsPopoverOpen}
          anchorEl={chipsContainerRef.current}
          onClose={() => setChipsPopoverOpen(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{mt: 1}}
        >
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{p: 1.5, flexWrap: 'wrap', maxWidth: '300px'}}
          >
            {metaChips.map(text => (
              <Chip key={text} size="small" variant="outlined" label={text} />
            ))}
          </Stack>
        </Popover>
      )}
      {item && (
        <Dialog
          open={debugModalOpen}
          onClose={() => setDebugModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Debug: Raw JSON</DialogTitle>
          <DialogContent>
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflow: 'auto',
                maxHeight: '60vh',
              }}
            >
              {JSON.stringify(item, null, 2)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDebugModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Wrapper>
  );
};

HousePreviewCardView.displayName = 'HousePreviewCardView';

const Wrapper = styled(Stack)`
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background-color: #fff;
  overflow: hidden;
  border-radius: 6px;
  @media (max-width: 512px) {
    border-radius: 0;
  }
`;

const BottomStack = styled(Stack)`
  background-color: #f5f5f5;
`;

const LikeButtonWrapper = styled.div`
  z-index: 10;
  flex-shrink: 0;
  margin-top: -42px;
  transform: translateX(8px);
`;

const LikeButton = styled(IconButton)`
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);`;
